import { useEffect, useRef } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useAuth } from '../auth/AuthContext';
import { authApi } from '../api/endpoints';
import { getApiErrorMessage } from '../api/client';
import { notify } from './Notifier';

interface OnboardingTourProps {
  /** Chỉ khởi chạy khi các phần tử data-tour mục tiêu đã có mặt trên DOM. */
  ready: boolean;
}

/**
 * Hướng dẫn sử dụng lần đầu (spotlight, thư viện driver.js) cho màn hình
 * nghiệm thu — chỉ khởi chạy khi tài khoản được Admin bật
 * (`show_onboarding_tour`) và chưa từng bấm "Không hướng dẫn nữa"
 * (`onboarding_tour_dismissed`). Đóng bằng nút X / phím ESC / click ra ngoài
 * chỉ ẩn tạm thời cho phiên hiện tại (driver.js hành xử mặc định, không gọi
 * API) — CHỈ nút "Không hướng dẫn nữa" ở bước cuối mới tắt vĩnh viễn.
 */
export function OnboardingTour({ ready }: OnboardingTourProps): JSX.Element | null {
  const { user, refresh } = useAuth();
  const startedRef = useRef(false);

  useEffect(() => {
    if (!ready || !user || startedRef.current) return;
    if (user.show_onboarding_tour !== 1 || user.onboarding_tour_dismissed !== 0) return;
    startedRef.current = true;

    const dismiss = async () => {
      try {
        await authApi.dismissOnboardingTour();
        await refresh();
      } catch (err) {
        notify(getApiErrorMessage(err, 'Không thể lưu trạng thái hướng dẫn'), 'error');
      }
    };

    const tour = driver({
      showProgress: true,
      progressText: '{{current}} / {{total}}',
      overlayOpacity: 0.6,
      skipMissingElement: true,
      nextBtnText: 'Tiếp theo',
      prevBtnText: 'Quay lại',
      steps: [
        {
          element: '[data-tour="feature-tree"]',
          popover: {
            title: 'Danh sách tính năng',
            description: 'Chọn một tính năng trong cây Giai đoạn → Tính năng để xem chi tiết.',
          },
        },
        {
          element: '[data-tour="feature-images"]',
          popover: {
            title: 'Ảnh minh hoạ',
            description: 'Xem ảnh minh hoạ để hiểu rõ tính năng trước khi nghiệm thu.',
          },
        },
        {
          element: '[data-tour="feature-choice"]',
          popover: {
            title: 'Xác nhận nghiệm thu',
            description:
              'Chọn "Đã xác nhận" nếu tính năng đáp ứng yêu cầu, hoặc "Yêu cầu thay đổi" nếu cần chỉnh sửa.',
          },
        },
        {
          element: '[data-tour="feature-submit"]',
          popover: {
            title: 'Gửi kết quả',
            description: 'Bấm nút này để gửi kết quả nghiệm thu của bạn.',
            doneBtnText: 'Không hướng dẫn nữa',
            onDoneClick: () => {
              void dismiss();
              tour.destroy();
            },
          },
        },
      ],
    });

    tour.drive();

    return () => {
      tour.destroy();
    };
  }, [ready, user, refresh]);

  return null;
}
